import React, { useEffect, useRef, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { get } from 'lodash';
import moment from 'moment';
import workerScript from '../workers/notificationWorker';
import { fetchExpertBookingsAction } from '../actions/partner';
import { apiBaseUrl, appName as partnerAppName } from '../constants/configuration';
import { apiResponseCodes, storageTypes } from '../constants/globals';

import { getItem } from '../utils/storage';
import resourcesConfig from '../resources';
import { isNativeWebView } from '../utils/helpers';
import BookingContext from './BookingContext';
import { nativeEventType } from '../config';
import {
  AUTH_TOKEN_UPDATED_EVENT,
  requestWorkerTokenRefresh,
} from '../utils/authTokenSync';
import newBookingSound from '../assets/sounds/new-booking-received.mp3';
import serviceCompletedSound from '../assets/sounds/service-end-notification.mp3';

const getValidPollingTime = (value, fallbackValue) => {
  const pollingTime = Number(value);
  if (Number.isFinite(pollingTime) && pollingTime > 0) {
    return pollingTime;
  }

  return Number(fallbackValue);
};

const BookingProvider = ({ children, resources, locale, authData, actions }) => {
  // In the hybrid app, native Home is the single owner of assigned-booking
  // polling and reminder audio. Keeping the hidden WebView worker active causes
  // duplicate reminder initialization and competing service transitions.
  const nativeOwnsBookingPolling = isNativeWebView;
  const [activeBooking, setActiveBooking] = useState(null);
  const reduxAccessToken = get(authData, 'accessToken', '');
  const persistedAccessToken = get(
    getItem(storageTypes.localStorage, 'AuthData'),
    'accessToken',
    '',
  );
  const pollingAccessToken = reduxAccessToken || persistedAccessToken;
  const hasAccessToken = !!pollingAccessToken;

  const bookingWorkerRef = useRef(null);
  const bookingPollingFallbackRef = useRef(null);
  const bookingAudioRef = useRef(new Audio(newBookingSound));
  const serviceCompletedAudioRef = useRef(new Audio(serviceCompletedSound));
  const bookingReminderIntervalRef = useRef(null);
  const activeBookingRef = useRef(null);
  const hasPlayedServiceCompletionAlertRef = useRef(false);
  const lastPlayedBookingIdRef = useRef(null);
  const serviceTransitionRef = useRef(null);
  /**
   * booking config
   */
  const bookingConfig = {
    bookingReminderIntervalInMinutes: Math.max(
      1,
      Number(
        get(
          resources,
          'bookingReminderIntervalInMinutes',
          get(resourcesConfig, 'bookingReminderIntervalInMinutes', 1),
        ),
      ),
    ),

    serviceCompletionAlertBeforeSeconds: Number(
      get(
        resources,
        'serviceCompletionAlertBeforeSeconds',
        get(resourcesConfig, 'serviceCompletionAlertBeforeSeconds', 270),
      ),
    ),

    expertBookingPollingTime: getValidPollingTime(
      get(resources, 'expertBookingPollingTime'),
      get(resourcesConfig, 'expertBookingPollingTime'),
    ),
    expertBookingPendingPollingTime: getValidPollingTime(
      get(resources, 'expertBookingPendingPollingTime'),
      get(resourcesConfig, 'expertBookingPendingPollingTime', 60000),
    ),
    expertBookingMaxPollingTime: getValidPollingTime(
      get(resources, 'expertBookingMaxPollingTime'),
      get(resourcesConfig, 'expertBookingMaxPollingTime', 300000),
    ),
    expertBookingPollingJitterRatio: Number(
      get(resources, 'expertBookingPollingJitterRatio', 0.1),
    ),
  };

  useEffect(() => {
    console.info('[BookingPolling] Resolved polling resource', {
      locale,
      resourceValue: get(resources, 'expertBookingPollingTime'),
      pollingTime: bookingConfig.expertBookingPollingTime,
    });
  }, [locale, resources, bookingConfig.expertBookingPollingTime]);

  /**
   * preload audio
   */
  useEffect(() => {
    bookingAudioRef.current.preload = 'auto';
    serviceCompletedAudioRef.current.preload = 'auto';
    bookingAudioRef.current.volume = 1;
    serviceCompletedAudioRef.current.volume = 1;
    bookingAudioRef.current.load();
    serviceCompletedAudioRef.current.load();
  }, []);

  useEffect(() => {
    const activeBookingSession = get(activeBooking, 'bookingSessionResponse', {});
    const expectedBookingEndTime = get(activeBookingSession, 'expectedBookingEndTime');
    if (!expectedBookingEndTime) {
      hasPlayedServiceCompletionAlertRef.current = false;
      return;
    }

    const interval = setInterval(() => {
      const remainingSeconds = Math.max(
        0,
        moment(expectedBookingEndTime).diff(moment(), 'seconds'),
      );

      const shouldPlayAlert =
        remainingSeconds <= bookingConfig.serviceCompletionAlertBeforeSeconds &&
        remainingSeconds > 0;

      if (shouldPlayAlert && !hasPlayedServiceCompletionAlertRef.current) {
        hasPlayedServiceCompletionAlertRef.current = true;

        if (isNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({
              eventType: {
                type: nativeEventType.serviceCompletionAlert,
                data: {
                  bookingId: get(activeBookingRef.current, 'bookingId'),
                },
              },
            }),
          );
        } else {
          playServiceCompletedSound();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [activeBooking, bookingConfig.serviceCompletionAlertBeforeSeconds]);

  /**
   * play booking sound
   */
  const playBookingSound = () => {
    try {
      bookingAudioRef.current.pause();
      bookingAudioRef.current.currentTime = 0;
      const audio = bookingAudioRef.current.cloneNode();
      audio.volume = 1;
      const playPromise = audio.play();

      if (playPromise) {
        playPromise.catch((error) => {
          console.log('BOOKING AUDIO FAILED', error);
        });
      }
    } catch (error) {
      // ignore
    }
  };

  /**
   * play service completion sound
   */
  const playServiceCompletedSound = () => {
    try {
      serviceCompletedAudioRef.current.pause();
      serviceCompletedAudioRef.current.currentTime = 0;
      const audio = serviceCompletedAudioRef.current.cloneNode();
      audio.volume = 1;
      const playPromise = audio.play();

      if (playPromise) {
        playPromise.catch((error) => {
          console.log('SERVICE AUDIO FAILED', error);
        });
      }
    } catch (error) {
      // ignore
    }
  };

  /**
   * clear reminder
   */
  const clearBookingReminder = (notifyNative = true) => {
    if (bookingReminderIntervalRef.current) {
      clearInterval(bookingReminderIntervalRef.current);

      bookingReminderIntervalRef.current = null;

      if (isNativeWebView && notifyNative) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            eventType: {
              type: nativeEventType.bookingReminderStopped,
              data: {
                bookingId: get(activeBookingRef.current, 'bookingId'),
              },
            },
          }),
        );
      }
    }
  };

  /**
   * reminder sound
   */
  const startBookingReminderSound = (bookingId) => {
    // The new-booking event schedules the replacement native reminder.
    // Do not race it with a stop event from the previous web interval.
    clearBookingReminder(false);

    bookingReminderIntervalRef.current = setInterval(() => {
      const latestBooking = activeBookingRef.current;
      const latestBookingId = get(latestBooking, 'bookingId');
      const latestBookingStatus = get(latestBooking, 'status');

      if (latestBookingId !== bookingId || latestBookingStatus !== 'CONFIRMED') {
        clearBookingReminder();
        return;
      }

      if (isNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            eventType: {
              type: nativeEventType.bookingReminder,
              data: {
                bookingId,
              },
            },
          }),
        );
      } else {
        playBookingSound();
      }
    }, bookingConfig.bookingReminderIntervalInMinutes * 60 * 1000);
  };

  /**
   * stop polling
   */
  const stopExpertBookingPolling = () => {
    if (bookingPollingFallbackRef.current) {
      clearInterval(bookingPollingFallbackRef.current);
      bookingPollingFallbackRef.current = null;
    }

    if (bookingWorkerRef.current) {
      bookingWorkerRef.current.postMessage({
        eventType: 'STOP',
      });
    }
  };

  /**
   * start polling
   */
  const startExpertBookingPolling = ({ accessToken, pollingTime }) => {
    if (!accessToken || nativeOwnsBookingPolling) {
      return;
    }

    if (bookingPollingFallbackRef.current) {
      clearInterval(bookingPollingFallbackRef.current);
      bookingPollingFallbackRef.current = null;
    }

    if (bookingWorkerRef.current) {
      const nativeAppConfig = window.nativeAppConfig || {};
      bookingWorkerRef.current.postMessage({
        eventType: 'polling',

        srcUrl: `${apiBaseUrl}/api/v1/expert/bookings/ASSIGNED`,

        accessToken,

        pollingTime,
        pendingPollingTime: bookingConfig.expertBookingPendingPollingTime,
        maxPollingTime: bookingConfig.expertBookingMaxPollingTime,
        jitterRatio: bookingConfig.expertBookingPollingJitterRatio,
        appName: get(nativeAppConfig, 'appName', partnerAppName),
        appId: get(nativeAppConfig, 'appId', ''),
      });
      return;
    }

    console.warn('[BookingPolling] Worker unavailable; using main-thread polling');
    refreshExpertBooking();
    bookingPollingFallbackRef.current = setInterval(() => {
      refreshExpertBooking();
    }, pollingTime);
  };

  useEffect(() => {
    window.startExpertBookingPolling = (accessToken) => {
      if (get(activeBookingRef.current, 'status') !== 'IN_PROGRESS') {
        startExpertBookingPolling({
          accessToken,
          pollingTime: bookingConfig.expertBookingPollingTime,
        });
      }
    };

    window.stopExpertBookingPolling = () => {
      stopExpertBookingPolling();
    };

    return () => {
      delete window.startExpertBookingPolling;
      delete window.stopExpertBookingPolling;
    };
  }, [bookingConfig.expertBookingPollingTime]);

  useEffect(() => {
    const handleAuthTokenUpdated = (event) => {
      const accessToken = get(event, 'detail.accessToken', '');
      if (!accessToken || !bookingWorkerRef.current) return;
      bookingWorkerRef.current.postMessage({ eventType: 'update-token', accessToken });
    };

    window.addEventListener(AUTH_TOKEN_UPDATED_EVENT, handleAuthTokenUpdated);
    return () => window.removeEventListener(AUTH_TOKEN_UPDATED_EVENT, handleAuthTokenUpdated);
  }, []);

  const refreshExpertBooking = () => {
    const url = '/api/v1/expert/bookings/ASSIGNED';
    return actions
      .fetchExpertBookings({
        url,
      })
      .then((response) => {
        if (!apiResponseCodes.SUCCESS.includes(get(response, 'status'))) {
          return;
        }
        const bookings = get(response, 'data._embedded.expertBookingResponses', []);
        handleBookingPollingResponse({
          _embedded: {
            expertBookingResponses: bookings,
          },
        });
      });
  };

  useEffect(() => {
    const handleAppBecameActive = () => {
      const authData = getItem(storageTypes.localStorage, 'AuthData');
      const accessToken = get(authData, 'accessToken', '');

      if (!accessToken) return;

      if (get(activeBookingRef.current, 'status') !== 'IN_PROGRESS') {
        startExpertBookingPolling({
          accessToken,
          pollingTime: bookingConfig.expertBookingPollingTime,
        });
      }
    };

    window.addEventListener(nativeEventType.appBecameActive, handleAppBecameActive);

    return () => {
      window.removeEventListener(nativeEventType.appBecameActive, handleAppBecameActive);
    };
  }, [bookingConfig.expertBookingPollingTime]);

  /**
   * handle polling response
   */
  const handleBookingPollingResponse = (response) => {
    const bookings = get(response, '_embedded.expertBookingResponses', []);
    const currentBooking = bookings[0] || null;

    if (!currentBooking) {
      const hasRunningService = get(serviceTransitionRef.current, 'type') === 'STARTING';
      if (hasRunningService && activeBookingRef.current) {
        return;
      }
      lastPlayedBookingIdRef.current = null;
      setActiveBooking(null);
      activeBookingRef.current = null;
      clearBookingReminder();
      return;
    }
    const bookingStatus = get(currentBooking, 'status');
    const bookingId = get(currentBooking, 'bookingId');
    const previousBookingId = get(activeBookingRef.current, 'bookingId');
    const previousBookingStatus = get(activeBookingRef.current, 'status');

    if (bookingStatus === 'IN_PROGRESS') {
      serviceTransitionRef.current = null;
      activeBookingRef.current = currentBooking;
      clearBookingReminder();
      stopExpertBookingPolling();
      setActiveBooking({ ...currentBooking });
      return;
    }
    if (bookingStatus === 'CONFIRMED' && lastPlayedBookingIdRef.current !== bookingId) {
      if (isNativeWebView) {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            eventType: {
              type: nativeEventType.newBookingReceived,
              data: {
                bookingId,
              },
            },
          }),
        );
      } else {
        playBookingSound();
      }
      startBookingReminderSound(bookingId);
      lastPlayedBookingIdRef.current = bookingId;
    }
    /**
     * update booking
     */
    const hasServiceTransition = get(serviceTransitionRef.current, 'bookingId') === bookingId;
    if (
      previousBookingId !== bookingId ||
      previousBookingStatus !== bookingStatus ||
      hasServiceTransition
    ) {
      setActiveBooking({ ...currentBooking });
    }

    activeBookingRef.current = currentBooking;

    if (bookingStatus !== 'IN_PROGRESS') {
      hasPlayedServiceCompletionAlertRef.current = false;
    }
  };

  /**
   * initialize worker
   */
  useEffect(() => {
    if (nativeOwnsBookingPolling) {
      stopExpertBookingPolling();
      return undefined;
    }
    const accessToken = pollingAccessToken;

    if (!accessToken) {
      stopExpertBookingPolling();
      return;
    }

    /**
     * create worker
     */
    if (!bookingWorkerRef.current) {
      try {
        bookingWorkerRef.current = new Worker(workerScript);
      } catch (error) {
        console.error('[BookingPolling] Unable to create booking worker', error);
        bookingWorkerRef.current = null;
      }

      if (bookingWorkerRef.current) {
        bookingWorkerRef.current.onmessage = (event) => {
          const workerMessage = get(event, 'data', {});
          if (get(workerMessage, 'eventType') === 'auth-refresh-required') {
            requestWorkerTokenRefresh(workerMessage).catch((error) => {
              console.error('[Auth][Polling] Unable to refresh polling credentials', error);
            });
            return;
          }
          if (get(workerMessage, 'eventType') === 'polling-status') {
            console.info('[BookingPolling] Worker acknowledged polling start', {
              srcUrl: get(workerMessage, 'srcUrl', ''),
              pollingTime: get(workerMessage, 'pollingTime'),
            });
            return;
          }
          if (get(workerMessage, 'eventType') === 'polling-scheduled') {
            console.info('[BookingPolling] Next worker poll scheduled', {
              pollingTime: Math.round(get(workerMessage, 'pollingTime', 0)),
            });
            return;
          }
          if (get(workerMessage, 'eventType') === 'auth-rejected') {
            console.error(
              '[BookingPolling] Worker authentication retry rejected; continuing scheduled polling',
            );
            return;
          }
          if (get(workerMessage, 'eventType') === 'polling-error') {
            console.warn('[BookingPolling] Assigned-booking request failed', {
              message: get(workerMessage, 'message', 'Unknown polling error'),
            });
            return;
          }
          handleBookingPollingResponse(workerMessage);
        };

        bookingWorkerRef.current.onerror = (error) => {
          console.error('[BookingPolling] Worker failed; switching to fallback polling', error);
          bookingWorkerRef.current.terminate();
          bookingWorkerRef.current = null;
          startExpertBookingPolling({
            accessToken,
            pollingTime: bookingConfig.expertBookingPollingTime,
          });
        };
      }
    }
    console.info('[BookingPolling] Starting assigned-booking polling', {
      pollingTime: bookingConfig.expertBookingPollingTime,
    });
    const hasRunningService = get(activeBookingRef.current, 'status') === 'IN_PROGRESS';
    if (!hasRunningService) {
      startExpertBookingPolling({
        accessToken,
        pollingTime: bookingConfig.expertBookingPollingTime,
      });
    }

    /**
     * cleanup
     */
    return () => {
      console.info('[BookingPolling] Stopping assigned-booking polling');
      stopExpertBookingPolling();

      clearBookingReminder();
    };
  }, [bookingConfig.expertBookingPollingTime, hasAccessToken]);

  /**
   * context value
   */
  const markServiceStarting = (bookingId) => {
    serviceTransitionRef.current = {
      bookingId,
      type: 'STARTING',
    };
  };

  const removeUnavailableBooking = (bookingId) => {
    if (get(activeBookingRef.current, 'bookingId') !== bookingId) return;
    serviceTransitionRef.current = null;
    activeBookingRef.current = null;
    setActiveBooking(null);
    lastPlayedBookingIdRef.current = null;
    clearBookingReminder();
  };

  const clearServiceTransition = () => {
    serviceTransitionRef.current = null;
  };

  const value = useMemo(
    () => ({
      activeBooking,
      refreshExpertBooking,
      markServiceStarting,
      clearServiceTransition,
      removeUnavailableBooking,
    }),
    [activeBooking],
  );

  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
};

const mapStateToProps = (state) => ({
  resources: get(state, 'common.resources', {}),
  locale: get(state, 'common.locale', 'hi'),
  authData: get(state, 'authentication.authData', {}),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchExpertBookings: fetchExpertBookingsAction,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(BookingProvider);
